import { MOCK_PEOPLE, type MockPerson } from '@lg/shared';

export interface DirectoryUser {
  upn: string;
  displayName: string;
  enabled: boolean;
}

/** Entra ID lookups used by people rule P2 (spec 12.4). */
export interface DirectoryClient {
  getUserByUpn(upn: string): Promise<DirectoryUser | null>;
}

/** 25 fictional directory users, two of them disabled. */
export class MockDirectoryClient implements DirectoryClient {
  private readonly users: Map<string, DirectoryUser>;

  constructor(people: readonly MockPerson[] = MOCK_PEOPLE) {
    this.users = new Map(
      people.map((p) => [
        p.upn,
        { upn: p.upn, displayName: `${p.firstName} ${p.lastName}`, enabled: p.enabled },
      ]),
    );
  }

  async getUserByUpn(upn: string): Promise<DirectoryUser | null> {
    return this.users.get(upn.trim().toLowerCase()) ?? null;
  }
}

/**
 * Caches lookups for a fixed window (spec: 10 minutes per import), including
 * "not found" answers.
 */
export class CachedDirectoryClient implements DirectoryClient {
  private readonly cache = new Map<string, { at: number; value: DirectoryUser | null }>();

  constructor(
    private readonly inner: DirectoryClient,
    private readonly ttlMs = 10 * 60 * 1000,
    private readonly now: () => number = () => Date.now(),
  ) {}

  async getUserByUpn(upn: string): Promise<DirectoryUser | null> {
    const key = upn.trim().toLowerCase();
    const hit = this.cache.get(key);
    if (hit && this.now() - hit.at < this.ttlMs) return hit.value;
    const value = await this.inner.getUserByUpn(key);
    this.cache.set(key, { at: this.now(), value });
    return value;
  }
}

export interface DirectoryEnv {
  DIRECTORY_MODE?: string;
}

export function createDirectoryClient(env: DirectoryEnv): DirectoryClient {
  const mode = env.DIRECTORY_MODE ?? 'mock';
  if (mode === 'mock') return new MockDirectoryClient();
  if (mode === 'live') {
    // GraphDirectoryClient is a Phase 2 checklist item (spec 12.4).
    throw new Error('DIRECTORY_MODE=live is not available yet: GraphDirectoryClient is built in Phase 2.');
  }
  throw new Error(`DIRECTORY_MODE must be mock or live, got "${mode}"`);
}
